import React, { useState, useEffect, useCallback } from 'react';
import { DashboardCard, StatsGrid, StatItem, DataList, DataListItem } from '../../../shared/ui';
import { StatusActionButton, BottomSheet } from '../../../shared/ui';
import { 
  incomingPaymentsV2Api, 
  IncomingPaymentV2, 
  CreateIncomingPaymentV2Request 
} from '../../../api/incomingPaymentsV2Api';
import { typesOfWorkApi, TypeOfWork } from '../../../api/typesOfWorkApi';
import { useAuth } from '../../../shared/contexts/AuthContext';
import './IncomingPaymentsV2Card.css';

interface IncomingPaymentsV2CardProps {
  siteId: number;
}

const IncomingPaymentsV2Card: React.FC<IncomingPaymentsV2CardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false); // Отслеживаем открытие fullscreen
  const [incomingPayments, setIncomingPayments] = useState<IncomingPaymentV2[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showPaymentMenu, setShowPaymentMenu] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<IncomingPaymentV2 | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  
  // Состояние для формы создания платежа
  const [newPayment, setNewPayment] = useState<CreateIncomingPaymentV2Request>({
    typeOfWorkId: 0,
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    description: '',
    paymentMethod: '',
    invoiceNumber: '',
    clientName: '',
    isConfirmed: false
  });
  
  // Списки для выбора
  const [typesOfWork, setTypesOfWork] = useState<TypeOfWork[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  const { user } = useAuth();

  // Загрузка прав доступа
  const loadPermissions = useCallback(async () => {
    try {
      const hasPermission = await incomingPaymentsV2Api.checkEditPermission(siteId);
      setCanEdit(hasPermission);
    } catch (error) {
      console.error('Ошибка при проверке прав:', error);
      setCanEdit(false);
    }
  }, [siteId]);

  // Загрузка платежей клиентов
  const loadIncomingPayments = useCallback(async () => {
    console.log('loadIncomingPayments вызвана для объекта', siteId);
    
    if (!user?.id) {
      console.log('Пользователь не готов, пропускаем загрузку');
      return;
    }
    
    console.log('Начинаем загрузку входящих платежей для объекта', siteId);
    setIsLoading(true);
    try {
      const paymentsData = await incomingPaymentsV2Api.getIncomingPaymentsForSite(siteId);
      console.log('Получены входящие платежи для объекта:', paymentsData);
      setIncomingPayments(paymentsData);
    } catch (error) {
      console.error('Ошибка при загрузке входящих платежей:', error);
    } finally {
      setIsLoading(false);
    }
  }, [siteId, user]);

  // Ленивая загрузка: загружаем данные когда карточка раскрыта (inline) или fullscreen модальное окно открыто
  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      loadIncomingPayments();
      loadPermissions();
    }
  }, [isCollapsed, isFullscreenOpen, loadIncomingPayments, loadPermissions]);

  // Загрузка данных для формы
  const loadFormData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const typesOfWorkData = await typesOfWorkApi.getAllTypesOfWork();
      setTypesOfWork(typesOfWorkData.filter(type => !type.closed));
    } catch (error) {
      console.error('Ошибка при загрузке данных формы:', error);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  const handleCreatePayment = async () => {
    if (!newPayment.typeOfWorkId || newPayment.amount <= 0 || !newPayment.description.trim()) {
      return;
    }
    
    setIsCreating(true);
    try {
      await incomingPaymentsV2Api.createIncomingPayment(siteId, newPayment);
      console.log('Платеж успешно создан');
      
      // После создания обновляем список
      await loadIncomingPayments();
      
      // Сбрасываем форму
      setNewPayment({
        typeOfWorkId: 0,
        amount: 0,
        paymentDate: new Date().toISOString().split('T')[0],
        description: '',
        paymentMethod: '',
        invoiceNumber: '',
        clientName: '',
        isConfirmed: false
      });
      setShowCreateMenu(false);
    } catch (error) {
      console.error('Ошибка при создании платежа:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!selectedPayment) return;

    try {
      await incomingPaymentsV2Api.deleteIncomingPayment(siteId, selectedPayment.id);
      console.log('Платеж успешно удален');
      
      // После удаления обновляем список
      await loadIncomingPayments();
      setShowPaymentMenu(false);
      setSelectedPayment(null);
    } catch (error) {
      console.error('Ошибка при удалении платежа:', error);
    }
  };

  const handleInputChange = (field: keyof CreateIncomingPaymentV2Request, value: string | number | boolean) => {
    setNewPayment(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePaymentClick = (payment: IncomingPaymentV2) => {
    setSelectedPayment(payment);
    setShowPaymentMenu(true);
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  // Подготовка данных для статистики
  const totalAmount = incomingPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const paymentsCount = incomingPayments.length;
  const thisMonth = new Date();
  thisMonth.setDate(1);
  const thisMonthPayments = incomingPayments.filter(p => 
    new Date(p.paymentDate) >= thisMonth
  );
  const thisMonthAmount = thisMonthPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const thisMonthCount = thisMonthPayments.length;

  const statsData: StatItem[] = [
    {
      id: 'total-amount',
      label: 'Общая сумма',
      value: formatAmount(totalAmount),
      icon: 'ri-money-dollar-circle-line',
      variant: 'default'
    },
    {
      id: 'total-count',
      label: 'Всего платежей',
      value: paymentsCount.toString(),
      icon: 'ri-file-list-line',
      variant: 'default'
    },
    {
      id: 'month-amount',
      label: 'За этот месяц',
      value: formatAmount(thisMonthAmount),
      icon: 'ri-calendar-line',
      variant: thisMonthAmount > 0 ? 'large' : 'default'
    },
    {
      id: 'month-count',
      label: 'Платежей в месяце',
      value: thisMonthCount.toString(),
      icon: 'ri-time-line',
      variant: 'default'
    }
  ];

  // Сортируем платежи по дате (новые сверху)
  const sortedPayments = [...incomingPayments].sort((a, b) => 
    new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
  );

  // Подготовка данных для DataList
  const prepareDataListItems = (): DataListItem[] => {
    return sortedPayments.map((payment) => {
      const typeOfWork = typesOfWork.find(t => t.id === payment.typeOfWorkId);
      
      return {
        id: payment.id,
        title: formatAmount(payment.amount),
        subtitle: typeOfWork ? `${typeOfWork.name}${typeOfWork.section ? ` (${typeOfWork.section})` : ''}` : 'Вид работы',
        details: [
          {
            icon: 'ri-calendar-line',
            text: formatDate(payment.paymentDate)
          },
          ...(payment.description ? [{
            icon: 'ri-file-text-line',
            text: payment.description
          }] : []),
          ...(payment.clientName ? [{
            icon: 'ri-user-line',
            text: payment.clientName
          }] : [])
        ],
        onClick: () => handlePaymentClick(payment)
      };
    });
  };

  if (isLoading) {
    return (
      <DashboardCard
        id="incoming-payments-v2-card"
        title="Платежи клиентов"
        icon="ri-bank-card-line"
        isCollapsible={true}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        expandMode="fullscreen"
        onFullscreenOpenChange={setIsFullscreenOpen}
        className="incoming-payments-v2-card"
      >
        <div className="loading-state">
          <i className="ri-loader-4-line"></i>
          <span>Загрузка платежей...</span>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard
      id="incoming-payments-v2-card"
      title="Платежи клиентов"
      icon="ri-bank-card-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      expandMode="fullscreen"
      onFullscreenOpenChange={setIsFullscreenOpen}
      className="incoming-payments-v2-card"
    >
      <div className="incoming-payments-overview">
        {canEdit && (
          <div className="incoming-payments-actions">
            <StatusActionButton
              variant="primary"
              fullWidth
              icon="ri-add-line"
              onClick={() => {
                setShowCreateMenu(true);
                loadFormData();
              }}
            >
              Новый платеж
            </StatusActionButton>
          </div>
        )}

        <StatsGrid items={statsData} />

        <DataList
          items={prepareDataListItems()}
          emptyState={{
            icon: 'ri-bank-card-line',
            title: 'Платежи не найдены',
            subtitle: canEdit ? 'Создайте первый платеж' : 'Пока нет платежей'
          }}
          variant="detailed"
          showDividers={true}
        />
      </div>

      {/* Контекстное меню создания платежа */}
      <BottomSheet
        isOpen={showCreateMenu}
        title="Новый платеж"
        onClose={() => {
          setShowCreateMenu(false);
          setNewPayment({
            typeOfWorkId: 0,
            amount: 0,
            paymentDate: new Date().toISOString().split('T')[0],
            description: '',
            paymentMethod: '',
            invoiceNumber: '',
            clientName: '',
            isConfirmed: false
          });
        }}
      >
        <div className="create-payment-form">
          <div className="form-group">
            <label htmlFor="payment-type">Вид работы *</label>
            <select
              id="payment-type"
              value={newPayment.typeOfWorkId}
              onChange={(e) => handleInputChange('typeOfWorkId', parseInt(e.target.value))}
              className="form-input"
            >
              <option value={0}>Выберите вид работы</option>
              {typesOfWork.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name} {type.section && `(${type.section})`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="payment-amount">Сумма (₽) *</label>
            <input
              id="payment-amount"
              type="number"
              value={newPayment.amount || ''}
              onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
              placeholder="Введите сумму платежа"
              className="form-input"
              min="0"
              step="0.01"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="payment-date">Дата платежа *</label>
            <input
              id="payment-date"
              type="date"
              value={newPayment.paymentDate}
              onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="payment-description">Описание *</label>
            <textarea
              id="payment-description"
              value={newPayment.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Введите описание платежа"
              className="form-input"
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="payment-client-name">Клиент</label>
            <input
              id="payment-client-name"
              type="text"
              value={newPayment.clientName || ''}
              onChange={(e) => handleInputChange('clientName', e.target.value)}
              placeholder="Имя клиента (необязательно)"
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="payment-method">Способ оплаты</label>
            <input
              id="payment-method"
              type="text"
              value={newPayment.paymentMethod || ''}
              onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
              placeholder="Наличные, безнал и т.д."
              className="form-input"
            />
          </div>
          
          <div className="form-actions">
            <StatusActionButton
              variant="secondary"
              onClick={() => {
                setShowCreateMenu(false);
                setNewPayment({
                  typeOfWorkId: 0,
                  amount: 0,
                  paymentDate: new Date().toISOString().split('T')[0],
                  description: '',
                  paymentMethod: '',
                  invoiceNumber: '',
                  clientName: '',
                  isConfirmed: false
                });
              }}
              disabled={isCreating}
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              variant="primary"
              onClick={handleCreatePayment}
              disabled={!newPayment.typeOfWorkId || newPayment.amount <= 0 || 
                       !newPayment.description.trim() || isCreating}
              loading={isCreating}
              icon="ri-add-line"
            >
              Создать
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Контекстное меню платежа */}
      <BottomSheet
        isOpen={showPaymentMenu}
        title={selectedPayment ? `${formatAmount(selectedPayment.amount)} - ${formatDate(selectedPayment.paymentDate)}` : 'Платеж'}
        onClose={() => setShowPaymentMenu(false)}
        size="auto"
      >
        <div className="payment-menu">
          <StatusActionButton
            variant="secondary"
            fullWidth
            icon="ri-eye-line"
            onClick={() => {
              console.log('Просмотр платежа:', selectedPayment);
              setShowPaymentMenu(false);
            }}
          >
            Детали платежа
          </StatusActionButton>
          
          {canEdit && (
            <>
              <StatusActionButton
                variant="secondary"
                fullWidth
                icon="ri-edit-line"
                onClick={() => {
                  console.log('Редактирование платежа:', selectedPayment);
                  setShowPaymentMenu(false);
                }}
              >
                Редактировать
              </StatusActionButton>
              
              <StatusActionButton
                variant="danger"
                fullWidth
                icon="ri-delete-bin-line"
                onClick={handleDeletePayment}
              >
                Удалить
              </StatusActionButton>
            </>
          )}
        </div>
      </BottomSheet>
    </DashboardCard>
  );
};

export default IncomingPaymentsV2Card;

